import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { clearAssetComment, updateAssetComment } from "@/api/Equipment";
import { Text } from "@medoma/components/Text/Text";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import TextArea from "@/components/TextArea/TextArea";
import Form from "@/components/Form/Form";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import styles from "./CommentEditor.module.scss";

type CommentEditorProps = {
  assetId: string;
  comment: string | null | undefined;
};

export const CommentEditor = ({ assetId, comment }: CommentEditorProps) => {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const trimmedComment = comment?.trim() ? comment : null;
  const { register, handleSubmit, reset } = useForm<{ comment: string }>({
    defaultValues: { comment: trimmedComment ?? "" },
  });

  const { mutate: saveComment, isPending: isSavingComment } = useMutation({
    mutationFn: updateAssetComment,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att spara kommentaren.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      setIsEditing(false);
    },
  });

  const { mutate: removeComment, isPending: isRemovingComment } = useMutation({
    mutationFn: clearAssetComment,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort kommentaren.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      reset({ comment: "" });
    },
  });

  const handleClear = async () => {
    const confirmed = await confirm({
      title: t`Ta bort kommentar`,
      message: t`Är du säker på att du vill ta bort kommentaren?`,
    });
    if (!confirmed) {
      return;
    }
    removeComment({ assetId });
  };

  return (
    <div className={styles.fieldWithAction}>
      {trimmedComment ? <Text element="span">{trimmedComment}</Text> : null}
      <div className={styles.actions}>
        {isEditing ? (
          <PlainButton
            size="small"
            onClick={() => {
              reset({ comment: trimmedComment ?? "" });
              setIsEditing(false);
            }}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <>
            <PlainButton size="small" onClick={() => setIsEditing(true)}>
              {trimmedComment ? (
                <Trans>Ändra kommentar</Trans>
              ) : (
                <Trans>Lägg till kommentar</Trans>
              )}
            </PlainButton>
            {trimmedComment ? (
              <PlainButton
                size="small"
                onClick={handleClear}
                disabled={isRemovingComment}
              >
                <Trans>Ta bort kommentar</Trans>
              </PlainButton>
            ) : null}
          </>
        )}
      </div>
      {isEditing ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(({ comment: nextComment }) => {
              saveComment({ assetId, comment: nextComment });
            })}
          >
            <Form.Row>
              <TextArea
                label={t`Kommentar`}
                showOptionalLabel
                {...register("comment")}
              />
            </Form.Row>
            <FilledButton type="submit" disabled={isSavingComment}>
              <Trans>Spara</Trans>
            </FilledButton>
          </Form>
        </div>
      ) : null}
    </div>
  );
};
